import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

export const INK = '#16231a';
export const MUTED = '#56625a';
export const GREEN = '#2e7d32';
export const DEEP = '#173d24';
export const MINT = '#e8f1e9';
export const PAPER = '#fbfaf6';
export const LINE = 'rgba(22, 35, 26, 0.12)';
export const LINE_ON_DARK = 'rgba(255, 255, 255, 0.18)';
export const ACCENT = '#ff6f00';
export const MOCK_SHADOW = '0 1px 2px rgba(22,35,26,0.06), 0 18px 40px -18px rgba(22,35,26,0.35)';
export const HERO_IMAGE = '/hero/hero-lawn.jpg';

export const primaryButtonSx = {
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '1rem',
  borderRadius: 2,
  px: 3,
  py: 1.25,
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none' },
} as const;

export const secondaryButtonSx = {
  ...primaryButtonSx,
  borderColor: LINE,
  color: INK,
  bgcolor: '#fff',
  '&:hover': { borderColor: INK, bgcolor: '#fff' },
} as const;

export const MarketingPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ bgcolor: PAPER, color: INK, overflowX: 'hidden' }}>{children}</Box>
);

export const Eyebrow: React.FC<{ children: React.ReactNode; light?: boolean }> = ({ children, light }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
    <Box sx={{ width: 18, height: 2, bgcolor: ACCENT, borderRadius: 1 }} />
    <Typography
      component="span"
      sx={{
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: light ? '#b9d8bd' : GREEN,
      }}
    >
      {children}
    </Typography>
  </Box>
);

export const HeroTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    component="h1"
    sx={{
      fontWeight: 800,
      letterSpacing: '-0.035em',
      lineHeight: 1.04,
      color: INK,
      fontSize: { xs: '2.4rem', sm: '3rem', md: '3.6rem' },
      mb: 3,
    }}
  >
    {children}
  </Typography>
);

export const Highlight: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ color: GREEN }}>
    {children}
  </Box>
);

export const SectionTitle: React.FC<{ children: React.ReactNode; light?: boolean; component?: React.ElementType }> = ({
  children,
  light,
  component = 'h2',
}) => (
  <Typography
    component={component}
    sx={{
      fontWeight: 800,
      letterSpacing: '-0.025em',
      lineHeight: 1.1,
      color: light ? '#fff' : INK,
      fontSize: { xs: '1.85rem', sm: '2.25rem', md: '2.6rem' },
      mb: 2,
    }}
  >
    {children}
  </Typography>
);

export const Lede: React.FC<{ children: React.ReactNode; light?: boolean; maxWidth?: number }> = ({
  children,
  light,
  maxWidth = 560,
}) => (
  <Typography
    sx={{
      fontSize: { xs: '1.02rem', md: '1.1rem' },
      lineHeight: 1.65,
      color: light ? 'rgba(255,255,255,0.78)' : MUTED,
      maxWidth,
    }}
  >
    {children}
  </Typography>
);

export const PointList: React.FC<{ points: string[] }> = ({ points }) => (
  <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 3, display: 'grid', gap: 1.25 }}>
    {points.map((p) => (
      <Box
        component="li"
        key={p}
        sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', color: INK, fontSize: '0.975rem', lineHeight: 1.55 }}
      >
        <Box sx={{ width: 6, height: 6, bgcolor: GREEN, flexShrink: 0, transform: 'translateY(-2px)' }} />
        {p}
      </Box>
    ))}
  </Box>
);

/** Section heading on the left, supporting copy bottom-aligned on the right. */
export const SplitHeading: React.FC<{ eyebrow: string; title: string; body: React.ReactNode; light?: boolean }> = ({
  eyebrow,
  title,
  body,
  light,
}) => (
  <Grid container spacing={{ xs: 2, md: 6 }} sx={{ mb: { xs: 4, md: 6 } }}>
    <Grid item xs={12} md={6}>
      <Eyebrow light={light}>{eyebrow}</Eyebrow>
      <SectionTitle light={light}>{title}</SectionTitle>
    </Grid>
    <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'flex-end' }}>
      <Lede light={light}>{body}</Lede>
    </Grid>
  </Grid>
);

export const BuiltForStrip: React.FC<{ label: string; items: string[] }> = ({ label, items }) => (
  <Box sx={{ borderTop: `1px solid ${LINE}`, py: 3 }}>
    <Container maxWidth="lg">
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1.5, md: 4 }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
      >
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: MUTED }}>
          {label}
        </Typography>
        {items.map((t) => (
          <Typography key={t} sx={{ fontSize: '0.95rem', fontWeight: 600, color: INK }}>
            {t}
          </Typography>
        ))}
      </Stack>
    </Container>
  </Box>
);

export const FeatureRow: React.FC<{
  id?: string;
  eyebrow: string;
  title: string;
  body: string;
  points: string[];
  visual: React.ReactNode;
  reverse?: boolean;
}> = ({ id, eyebrow, title, body, points, visual, reverse }) => (
  <Box id={id} component="section" sx={{ py: { xs: 7, md: 11 }, borderTop: `1px solid ${LINE}` }}>
    <Container maxWidth="lg">
      <Grid container spacing={{ xs: 5, md: 10 }} alignItems="center">
        <Grid item xs={12} md={6}>
          <Eyebrow>{eyebrow}</Eyebrow>
          <SectionTitle>{title}</SectionTitle>
          <Lede>{body}</Lede>
          <PointList points={points} />
        </Grid>
        <Grid item xs={12} md={6} sx={{ order: { md: reverse ? -1 : 0 } }}>
          <Box sx={{ maxWidth: 460, mx: 'auto' }}>{visual}</Box>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

/* ---------- Product previews ---------- */

export const MockCard: React.FC<{ title: string; meta?: string; children: React.ReactNode; sx?: object }> = ({
  title,
  meta,
  children,
  sx,
}) => (
  <Box
    aria-hidden
    sx={{
      bgcolor: '#fff',
      border: `1px solid ${LINE}`,
      borderRadius: 3,
      boxShadow: MOCK_SHADOW,
      overflow: 'hidden',
      ...sx,
    }}
  >
    <Box
      sx={{
        px: 2.25,
        py: 1.5,
        borderBottom: `1px solid ${LINE}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: INK }}>{title}</Typography>
      {meta && <Typography sx={{ fontSize: '0.75rem', color: MUTED, whiteSpace: 'nowrap' }}>{meta}</Typography>}
    </Box>
    {children}
  </Box>
);

export type ChipTone = 'green' | 'amber' | 'grey' | 'red';
const CHIP_TONES: Record<ChipTone, { bg: string; fg: string }> = {
  green: { bg: '#e3f1e4', fg: '#1e6b25' },
  amber: { bg: '#fff1dc', fg: '#9a5200' },
  grey: { bg: '#eef0ee', fg: '#5b665e' },
  red: { bg: '#fde8e6', fg: '#a32a1c' },
};

export const StatusChip: React.FC<{ tone: ChipTone; children: React.ReactNode }> = ({ tone, children }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      px: 1,
      py: 0.25,
      borderRadius: 1,
      fontSize: '0.7rem',
      fontWeight: 700,
      whiteSpace: 'nowrap',
      bgcolor: CHIP_TONES[tone].bg,
      color: CHIP_TONES[tone].fg,
    }}
  >
    {children}
  </Box>
);

export const MockRow: React.FC<{ children: React.ReactNode; last?: boolean }> = ({ children, last }) => (
  <Box
    sx={{
      px: 2.25,
      py: 1.35,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      borderBottom: last ? 'none' : `1px solid ${LINE}`,
      fontSize: '0.85rem',
      color: INK,
    }}
  >
    {children}
  </Box>
);

export const MockPrimaryLine: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: INK }} noWrap>
      {title}
    </Typography>
    {subtitle && (
      <Typography sx={{ fontSize: '0.75rem', color: MUTED }} noWrap>
        {subtitle}
      </Typography>
    )}
  </Box>
);

/* ---------- Shared sections ---------- */

export const ComparisonTable: React.FC<{
  columns: string[];
  rows: string[][];
  highlightColumn?: number;
}> = ({ columns, rows, highlightColumn = 1 }) => {
  const firstWidth = 22;
  const otherWidth = (100 - firstWidth) / Math.max(1, columns.length - 1);
  return (
    <Box sx={{ mt: 5, overflowX: 'auto', border: `1px solid ${LINE}`, borderRadius: 3, bgcolor: '#fff' }}>
      <Box component="table" sx={{ width: '100%', minWidth: columns.length > 3 ? 720 : 560, borderCollapse: 'collapse' }}>
        <Box component="thead">
          <Box component="tr">
            {columns.map((h, i) => (
              <Box
                component="th"
                key={h || 'row'}
                scope="col"
                sx={{
                  textAlign: 'left',
                  px: 2.5,
                  py: 2,
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: i === highlightColumn ? '#fff' : INK,
                  bgcolor: i === highlightColumn ? GREEN : 'transparent',
                  borderBottom: `1px solid ${LINE}`,
                  width: `${i === 0 ? firstWidth : otherWidth}%`,
                }}
              >
                {h}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {rows.map((cells, idx) => (
            <Box component="tr" key={cells[0]}>
              {cells.map((cell, i) => (
                <Box
                  component={i === 0 ? 'th' : 'td'}
                  scope={i === 0 ? 'row' : undefined}
                  key={i}
                  sx={{
                    textAlign: 'left',
                    px: 2.5,
                    py: 1.75,
                    fontSize: '0.9rem',
                    fontWeight: i === 0 || i === highlightColumn ? 600 : 400,
                    color: i === 0 ? INK : i === highlightColumn ? DEEP : MUTED,
                    bgcolor: i === highlightColumn ? MINT : 'transparent',
                    borderBottom: idx === rows.length - 1 ? 'none' : `1px solid ${LINE}`,
                    verticalAlign: 'top',
                  }}
                >
                  {cell}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export interface FaqItem {
  q: string;
  a: string;
}

export const faqStructuredData = (faqs: FaqItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

export const FaqSection: React.FC<{ title: string; aside: React.ReactNode; faqs: FaqItem[] }> = ({ title, aside, faqs }) => (
  <Box component="section" sx={{ py: { xs: 7, md: 11 }, borderTop: `1px solid ${LINE}` }}>
    <Container maxWidth="lg">
      <Grid container spacing={{ xs: 4, md: 10 }}>
        <Grid item xs={12} md={4}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 96 } }}>
            <Eyebrow>FAQ</Eyebrow>
            <SectionTitle>{title}</SectionTitle>
            <Lede>{aside}</Lede>
          </Box>
        </Grid>
        <Grid item xs={12} md={8}>
          <Box sx={{ borderTop: `1px solid ${LINE}` }}>
            {faqs.map((f) => (
              <Accordion
                key={f.q}
                disableGutters
                elevation={0}
                square
                sx={{ bgcolor: 'transparent', borderBottom: `1px solid ${LINE}`, '&::before': { display: 'none' } }}
              >
                <AccordionSummary
                  expandIcon={<Add sx={{ color: INK }} />}
                  sx={{ px: 0, py: 1, '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': { transform: 'rotate(45deg)' } }}
                >
                  <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1.05rem', color: INK, pr: 2 }}>
                    {f.q}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 0, pb: 3 }}>
                  <Typography sx={{ color: MUTED, lineHeight: 1.7, maxWidth: 680 }}>{f.a}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

export const ClosingCta: React.FC<{
  title: string;
  body: string;
  primary: { label: string; onClick: () => void };
  secondary?: { label: string; to: string };
}> = ({ title, body, primary, secondary }) => (
  <Box
    component="section"
    sx={{
      color: '#fff',
      py: { xs: 8, md: 11 },
      backgroundImage: `linear-gradient(90deg, rgba(23,61,36,0.97) 0%, rgba(23,61,36,0.9) 45%, rgba(23,61,36,0.55) 100%), url("${HERO_IMAGE}")`,
      backgroundSize: 'cover',
      backgroundPosition: 'center 70%',
    }}
  >
    <Container maxWidth="lg">
      <Grid container spacing={4} alignItems="center">
        <Grid item xs={12} md={7}>
          <SectionTitle light>{title}</SectionTitle>
          <Lede light maxWidth={520}>
            {body}
          </Lede>
        </Grid>
        <Grid item xs={12} md={5}>
          <Stack direction={{ xs: 'column', sm: 'row', md: 'column', lg: 'row' }} spacing={1.5} justifyContent={{ md: 'flex-end' }}>
            <Button
              variant="contained"
              size="large"
              onClick={primary.onClick}
              sx={{ ...primaryButtonSx, bgcolor: '#fff', color: DEEP, '&:hover': { bgcolor: MINT, boxShadow: 'none' } }}
            >
              {primary.label}
            </Button>
            {secondary && (
              <Button
                component={RouterLink}
                to={secondary.to}
                variant="outlined"
                size="large"
                sx={{
                  ...primaryButtonSx,
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                {secondary.label}
              </Button>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

export const FinePrint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ py: 4 }}>
    <Container maxWidth="lg">
      <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.6, color: MUTED, maxWidth: 900 }}>{children}</Typography>
    </Container>
  </Box>
);
